import { Plus, Trash2, Loader2, Wand2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "./Textarea";
import { ergTypeIcon } from "./exerciseIcons";
import {
	ConditioningTemplateEditor,
	CONDITIONING_TYPES,
} from "../ConditioningTemplateEditor";
import {
	ERG_TYPES,
	BIKE_TYPES,
	ERG_CONDITIONING_TYPES,
	BIKE_LIKE_INTERVAL_TYPES,
	BIKE_LIKE_SIMPLE_TYPES,
	getErgTypeLabel,
	getErgMachineType,
	getErgSubtype,
	isErgMachineSubtypeType,
	isErgBenchmarkSelection,
	isErgMixedType,
	isErgBenchmarkType,
	getErgMetricsLabels,
	isAutoEnduranceName,
	nextEnduranceName,
} from "../../lib/programBuilderUtils";
import { EnduranceNameField } from "./EnduranceNameField";

/**
 * Bike / Erg workout configuration form. Extracted verbatim from the
 * ProgramBuilder "Add Exercise" dialog (gate `["bike","erg"].includes(...)`
 * stays in the parent). All state lives in the parent; behavior is identical.
 */
export function BikeErgForm({
	newExercise,
	setNewExercise,
	benchmarkOptions,
	exercises,
	setExercises,
	aiEnduranceParsing,
	handleParseEnduranceCustom,
	t,
}) {
	return (
		<div className="space-y-3">
			{/* Bike/Erg type selector */}
			<div className="space-y-2">
				<Label className="label-tactical">{t("pbTypologyLabel")}</Label>
				<div
					className={`grid gap-2 ${
						newExercise.exercise_type === "erg"
							? "grid-cols-2 md:grid-cols-3"
							: "grid-cols-2 sm:grid-cols-3 md:grid-cols-4"
					}`}
				>
					{(newExercise.exercise_type === "erg" ? ERG_TYPES : BIKE_TYPES).map(
						(bt) => (
							<button
								key={bt.value}
								onClick={() => {
									const resolvedBikeType =
										newExercise.exercise_type === "erg"
											? bt.value === "custom"
												? "custom"
												: bt.value === "benchmark"
													? "benchmark"
													: bt.value === "mixed_erg"
														? "mixed_erg"
														: `${bt.value}_endurance`
											: bt.value;
									const resolvedLabel =
										newExercise.exercise_type === "erg"
											? getErgTypeLabel(resolvedBikeType)
											: bt.label;
									const updates = {
										...newExercise,
										bike_type: resolvedBikeType,
										bike_terrain:
											newExercise.exercise_type === "erg"
												? null
												: newExercise.bike_terrain,
										exercise_name: nextEnduranceName(
											newExercise.exercise_name,
											`${
												newExercise.exercise_type === "erg" ? "Erg" : "Bike"
											} - ${resolvedLabel}`,
										),
									};
									if (
										BIKE_LIKE_INTERVAL_TYPES.includes(resolvedBikeType) &&
										(!newExercise.bike_intervals ||
											newExercise.bike_intervals.length === 0)
									) {
										updates.bike_intervals = [
											{
												repetitions: "",
												duration: "",
												intensity: "",
												cadence: "",
												power: "",
												recovery: "",
												notes: "",
											},
										];
									}
									setNewExercise(updates);
								}}
								className={`p-2 rounded-sm border text-xs text-center transition-colors ${
									(
										newExercise.exercise_type === "erg"
											? getErgMachineType(newExercise.bike_type) === bt.value
											: newExercise.bike_type === bt.value
									)
										? newExercise.exercise_type === "erg"
											? "border-cyan-400 bg-cyan-400/10 text-cyan-400"
											: "border-orange-400 bg-orange-400/10 text-orange-400"
										: newExercise.exercise_type === "erg"
											? "border-border text-muted-foreground hover:border-cyan-400/50"
											: "border-border text-muted-foreground hover:border-orange-400/50"
								}`}
							>
								{newExercise.exercise_type === "erg" &&
									(() => {
										const ErgIcon = ergTypeIcon(bt.value);
										return <ErgIcon className="w-3 h-3 mx-auto mb-1" />;
									})()}
								{t(bt.labelKey)}
							</button>
						),
					)}
				</div>
			</div>

			{newExercise.exercise_type === "erg" &&
				isErgMachineSubtypeType(newExercise.bike_type) &&
				!isErgBenchmarkSelection(newExercise.bike_type) && (
					<div className="space-y-1">
						<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
							{t("pbErgMode")}
						</span>
						<div className="flex gap-2">
							{[
								{
									value: "endurance",
									labelKey: "pbErgModeEndurance",
								},
								{
									value: "conditioning",
									labelKey: "pbErgModeConditioning",
								},
							].map((ergMode) => (
								<button
									key={ergMode.value}
									onClick={() => {
										const nextType = `${getErgMachineType(newExercise.bike_type)}_${ergMode.value}`;
										setNewExercise({
											...newExercise,
											bike_type: nextType,
											exercise_name: nextEnduranceName(
												newExercise.exercise_name,
												`Erg - ${getErgTypeLabel(nextType)}`,
											),
											bike_terrain: null,
										});
									}}
									className={`px-3 py-1 rounded-sm border text-[10px] uppercase tracking-wider transition-colors ${
										getErgSubtype(newExercise.bike_type) === ergMode.value
											? "border-cyan-400 bg-cyan-400/10 text-cyan-400"
											: "border-border text-muted-foreground hover:border-cyan-400/50"
									}`}
								>
									{t(ergMode.labelKey)}
								</button>
							))}
						</div>
					</div>
				)}

			{newExercise.exercise_type === "erg" &&
				isErgMixedType(newExercise.bike_type) && (
					<div className="space-y-2 border border-cyan-400/20 rounded-sm p-3 bg-cyan-400/5">
						<span className="text-[10px] text-cyan-400 uppercase tracking-wider">
							{t("pbErgMixedConfig")}
						</span>
						<ConditioningTemplateEditor
							value={newExercise}
							setValue={(updater) => {
								setNewExercise((prev) => {
									const next =
										typeof updater === "function" ? updater(prev) : updater;
									const typeLabel =
										CONDITIONING_TYPES.find(
											(ct) => ct.value === next.conditioning_type,
										)?.label || "";
									return {
										...next,
										bike_type: "mixed_erg",
										// Runs on every edit of the mixed config: a custom
										// name must survive it.
										exercise_name:
											typeLabel && isAutoEnduranceName(prev.exercise_name)
												? `Erg - Misto - ${typeLabel}`
												: prev.exercise_name,
										bike_terrain: null,
									};
								});
							}}
							exercises={exercises}
							showNameField={false}
							onExerciseCreated={(ex) => setExercises((prev) => [...prev, ex])}
						/>
					</div>
				)}

			{newExercise.bike_type && (
				<EnduranceNameField
					newExercise={newExercise}
					setNewExercise={setNewExercise}
					t={t}
				/>
			)}

			{/* Warmup — for all bike types except custom and benchmark */}
			{newExercise.bike_type &&
				newExercise.bike_type !== "custom" &&
				(newExercise.exercise_type === "erg"
					? !isErgBenchmarkType(newExercise.bike_type) &&
						!isErgMixedType(newExercise.bike_type)
					: newExercise.bike_type !== "benchmark") && (
					<div className="space-y-1">
						<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
							{t("warmup")}
						</span>
						<Input
							value={newExercise.warmup_description ?? ""}
							onChange={(e) =>
								setNewExercise({
									...newExercise,
									warmup_description: e.target.value,
								})
							}
							className="bg-secondary border-transparent rounded-sm text-xs"
							placeholder={t("pbBikeWarmupPlaceholder")}
						/>
					</div>
				)}

			{/* Terrain — only bike */}
			{newExercise.bike_type &&
				newExercise.exercise_type === "bike" &&
				newExercise.bike_type !== "custom" && (
					<div className="flex gap-2">
						{["indoor", "outdoor", "mixed"].map((t) => (
							<button
								key={t}
								onClick={() =>
									setNewExercise({
										...newExercise,
										bike_terrain: newExercise.bike_terrain === t ? null : t,
									})
								}
								className={`px-3 py-1 rounded-sm border text-[10px] uppercase tracking-wider transition-colors ${
									newExercise.bike_terrain === t
										? "border-orange-400 bg-orange-400/10 text-orange-400"
										: "border-border text-muted-foreground hover:border-orange-400/50"
								}`}
							>
								{t === "indoor"
									? "Indoor"
									: t === "outdoor"
										? "Outdoor"
										: "Misto"}
							</button>
						))}
					</div>
				)}

			{/* === INTERVALS: Multi-row bike/erg intervals === */}
			{BIKE_LIKE_INTERVAL_TYPES.includes(newExercise.bike_type) && (
				<div className="space-y-2">
					<div className="flex items-center justify-between">
						<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
							{newExercise.bike_type === "hill_repeats"
								? t("pbHillRepeats")
								: newExercise.bike_type === "threshold"
									? t("pbThresholdBlocks")
									: newExercise.bike_type === "vo2max"
										? t("pbVo2maxIntervals")
										: ERG_CONDITIONING_TYPES.includes(newExercise.bike_type)
											? t("pbErgConditioningBlocks")
											: t("pbIntervals")}
						</span>
						<Button
							variant="ghost"
							size="sm"
							onClick={() =>
								setNewExercise({
									...newExercise,
									bike_intervals: [
										...(newExercise.bike_intervals || []),
										{
											repetitions: 1,
											duration: "",
											intensity: "",
											cadence: "",
											power: "",
											recovery: "",
											notes: "",
										},
									],
								})
							}
							className="h-6 px-2 text-orange-400 hover:text-orange-300"
						>
							<Plus className="w-3 h-3 mr-1" /> {t("sets")}
						</Button>
					</div>
					{(newExercise.bike_intervals || []).map((row, ri) => (
						<div key={ri} className="bg-secondary/50 rounded-sm p-3 space-y-2">
							<div className="flex items-center justify-between">
								<span className="text-xs font-medium text-orange-400">
									Serie {ri + 1}
								</span>
								<button
									onClick={() => {
										const rows = [...(newExercise.bike_intervals || [])];
										rows.splice(ri, 1);
										setNewExercise({
											...newExercise,
											bike_intervals: rows,
										});
									}}
									className="text-muted-foreground hover:text-destructive"
								>
									<Trash2 className="w-3 h-3" />
								</button>
							</div>
							<div className="grid grid-cols-3 gap-2">
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("reps")}
									</span>
									<Input
										type="text"
										inputMode="numeric"
										value={row.repetitions ?? ""}
										onFocus={(e) => e.target.select()}
										onChange={(e) => {
											const rows = [...(newExercise.bike_intervals || [])];
											rows[ri] = {
												...rows[ri],
												repetitions:
													e.target.value === "" ? "" : Number(e.target.value),
											};
											setNewExercise({
												...newExercise,
												bike_intervals: rows,
											});
										}}
										className="bg-secondary border-transparent rounded-sm text-xs"
										placeholder="5"
									/>
								</div>
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("pbDurationOrDistance")}
									</span>
									<Input
										value={row.duration ?? ""}
										onChange={(e) => {
											const rows = [...(newExercise.bike_intervals || [])];
											rows[ri] = {
												...rows[ri],
												duration: e.target.value,
											};
											setNewExercise({
												...newExercise,
												bike_intervals: rows,
											});
										}}
										className="bg-secondary border-transparent rounded-sm text-xs"
										placeholder={
											newExercise.bike_type === "hill_repeats" ? "2km" : "5:00"
										}
									/>
								</div>
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("intensity")}
									</span>
									<Input
										value={row.intensity ?? ""}
										onChange={(e) => {
											const rows = [...(newExercise.bike_intervals || [])];
											rows[ri] = {
												...rows[ri],
												intensity: e.target.value,
											};
											setNewExercise({
												...newExercise,
												bike_intervals: rows,
											});
										}}
										className="bg-secondary border-transparent rounded-sm text-xs"
										placeholder="Z4, FTP 95%..."
									/>
								</div>
							</div>
							<div className="grid grid-cols-3 gap-2">
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("pbCadence")}
									</span>
									<Input
										value={row.cadence ?? ""}
										onChange={(e) => {
											const rows = [...(newExercise.bike_intervals || [])];
											rows[ri] = {
												...rows[ri],
												cadence: e.target.value,
											};
											setNewExercise({
												...newExercise,
												bike_intervals: rows,
											});
										}}
										className="bg-secondary border-transparent rounded-sm text-xs"
										placeholder={
											getErgMetricsLabels(newExercise.bike_type)
												.cadencePlaceholder
										}
									/>
								</div>
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("pbPower")}
									</span>
									<Input
										value={row.power ?? ""}
										onChange={(e) => {
											const rows = [...(newExercise.bike_intervals || [])];
											rows[ri] = {
												...rows[ri],
												power: e.target.value,
											};
											setNewExercise({
												...newExercise,
												bike_intervals: rows,
											});
										}}
										className="bg-secondary border-transparent rounded-sm text-xs"
										placeholder={
											getErgMetricsLabels(newExercise.bike_type)
												.powerPlaceholder
										}
									/>
								</div>
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("recovery")}
									</span>
									<Input
										value={row.recovery ?? ""}
										onChange={(e) => {
											const rows = [...(newExercise.bike_intervals || [])];
											rows[ri] = {
												...rows[ri],
												recovery: e.target.value,
											};
											setNewExercise({
												...newExercise,
												bike_intervals: rows,
											});
										}}
										className="bg-secondary border-transparent rounded-sm text-xs"
										placeholder="3:00 easy"
									/>
								</div>
							</div>
						</div>
					))}
				</div>
			)}

			{/* === ENDURANCE FIELDS === */}
			{BIKE_LIKE_SIMPLE_TYPES.includes(newExercise.bike_type) && (
				<div className="space-y-2">
					<div
						className={`grid ${newExercise.exercise_type === "erg" ? "grid-cols-2" : "grid-cols-3"} gap-3`}
					>
						<div className="space-y-1">
							<span className="text-xs text-muted-foreground">
								{t("distanceKmLabel")}
							</span>
							<Input
								type="text"
								inputMode="numeric"
								value={newExercise.distance_meters ?? ""}
								onChange={(e) =>
									setNewExercise({
										...newExercise,
										distance_meters:
											e.target.value === "" ? null : e.target.value,
									})
								}
								className="bg-secondary border-transparent rounded-sm"
								placeholder="60"
							/>
						</div>
						<div className="space-y-1">
							<span className="text-xs text-muted-foreground">
								{t("durationMin")}
							</span>
							<Input
								type="text"
								inputMode="numeric"
								value={newExercise.duration_minutes ?? ""}
								onChange={(e) =>
									setNewExercise({
										...newExercise,
										duration_minutes:
											e.target.value === "" ? null : e.target.value,
									})
								}
								className="bg-secondary border-transparent rounded-sm"
								placeholder="90"
							/>
						</div>
						{newExercise.exercise_type !== "erg" && (
							<div className="space-y-1">
								<span className="text-xs text-muted-foreground">
									{t("speedPace")}
								</span>
								<Input
									value={newExercise.target_pace ?? ""}
									onChange={(e) =>
										setNewExercise({
											...newExercise,
											target_pace: e.target.value,
										})
									}
									className="bg-secondary border-transparent rounded-sm"
									placeholder="30 km/h"
								/>
							</div>
						)}
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div className="space-y-1">
							<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
								{t("targetPower")}
							</span>
							<Input
								value={newExercise.bike_power_target ?? ""}
								onChange={(e) =>
									setNewExercise({
										...newExercise,
										bike_power_target: e.target.value,
									})
								}
								className="bg-secondary border-transparent rounded-sm text-xs"
								placeholder={
									newExercise.exercise_type === "erg"
										? getErgMetricsLabels(newExercise.bike_type)
												.powerPlaceholder
										: "Z2, FTP 75%, 180W..."
								}
							/>
						</div>
						<div className="space-y-1">
							<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
								{t("targetCadence")}
							</span>
							<Input
								value={newExercise.bike_cadence_target ?? ""}
								onChange={(e) =>
									setNewExercise({
										...newExercise,
										bike_cadence_target: e.target.value,
									})
								}
								className="bg-secondary border-transparent rounded-sm text-xs"
								placeholder={
									newExercise.exercise_type === "erg"
										? getErgMetricsLabels(newExercise.bike_type)
												.cadencePlaceholder
										: "85-95 rpm"
								}
							/>
						</div>
					</div>
				</div>
			)}

			{/* === CUSTOM — free-text workout === */}
			{newExercise.bike_type === "custom" && (
				<div className="space-y-1">
					<div className="flex items-center justify-between">
						<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
							{t("workoutDescription")}
						</span>
						{newExercise.exercise_type === "erg" && (
							<Button
								type="button"
								size="sm"
								variant="outline"
								className="h-7 px-2.5 gap-1.5 text-[10px] border-dashed border-acid/50 text-acid hover:text-acid/80 hover:bg-acid/10 rounded-sm"
								disabled={
									aiEnduranceParsing || !newExercise.custom_workout_text?.trim()
								}
								onClick={() => handleParseEnduranceCustom("erg")}
							>
								{aiEnduranceParsing ? (
									<>
										<Loader2 className="w-3 h-3 animate-spin" />
										{t("pbAiParsing")}
									</>
								) : (
									<>
										<Wand2 className="w-3 h-3" />
										{t("pbAiWand")}
									</>
								)}
							</Button>
						)}
					</div>
					<Textarea
						value={newExercise.custom_workout_text ?? ""}
						onChange={(e) =>
							setNewExercise({
								...newExercise,
								custom_workout_text: e.target.value,
							})
						}
						className="bg-secondary border-transparent rounded-sm text-xs min-h-[120px] resize-y md:resize overflow-auto"
						placeholder={
							newExercise.exercise_type === "erg"
								? t("pbErgFreePlaceholder")
								: t("pbBikeFreePlaceholder")
						}
					/>
				</div>
			)}

			{/* Cooldown — for all bike types except custom */}
			{newExercise.bike_type &&
				newExercise.bike_type !== "custom" &&
				(newExercise.exercise_type === "erg"
					? !isErgMixedType(newExercise.bike_type)
					: true) && (
					<div className="space-y-1">
						<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
							{t("cooldown")}
						</span>
						<Input
							value={newExercise.cooldown_description ?? ""}
							onChange={(e) =>
								setNewExercise({
									...newExercise,
									cooldown_description: e.target.value,
								})
							}
							className="bg-secondary border-transparent rounded-sm text-xs"
							placeholder={t("pbBikeCooldownPlaceholder")}
						/>
					</div>
				)}

			{/* === BENCHMARK EXERCISE PICKER (Bike/Erg) === */}
			{((newExercise.exercise_type === "erg" &&
				isErgBenchmarkSelection(newExercise.bike_type)) ||
				(newExercise.exercise_type === "bike" &&
					newExercise.bike_type === "benchmark")) && (
				<div className="space-y-3">
					<div className="space-y-1">
						<Label className="label-tactical">{t("pbSelectBenchmark")}</Label>
						{(() => {
							const bmType =
								newExercise.exercise_type === "erg" ? "erg" : "bike";
							const bmList = benchmarkOptions[bmType] || [];
							const borderColor = bmType === "erg" ? "cyan" : "orange";
							return bmList.length > 0 ? (
								<div className="grid grid-cols-2 gap-2">
									{bmList.map((bm) => (
										<button
											key={bm.exercise_id}
											onClick={() =>
												setNewExercise({
													...newExercise,
													benchmark_exercise_id: bm.exercise_id,
													benchmark_exercise_name: bm.name,
													exercise_name: `${bmType === "erg" ? "Erg" : "Bike"} Benchmark - ${bm.name}`,
												})
											}
											className={`p-2 rounded-sm border text-xs text-left transition-colors ${
												newExercise.benchmark_exercise_id === bm.exercise_id
													? `border-${borderColor}-400 bg-${borderColor}-400/10 text-${borderColor}-400`
													: `border-border text-muted-foreground hover:border-${borderColor}-400/50`
											}`}
										>
											{bm.name}
										</button>
									))}
								</div>
							) : (
								<p className="text-xs text-muted-foreground">
									Nessun benchmark configurato per {bmType}. Configurali dal
									pannello Admin.
								</p>
							);
						})()}
					</div>
				</div>
			)}

			{/* Pace Reference — for bike types only (not erg) */}
			{newExercise.exercise_type !== "erg" &&
				newExercise.bike_type &&
				(newExercise.exercise_type === "erg"
					? !isErgBenchmarkSelection(newExercise.bike_type) &&
						!isErgMixedType(newExercise.bike_type) &&
						newExercise.bike_type !== "custom"
					: newExercise.bike_type !== "benchmark" &&
						newExercise.bike_type !== "custom") &&
				(() => {
					const bmType = newExercise.exercise_type === "erg" ? "erg" : "bike";
					const bmList = benchmarkOptions[bmType] || [];
					const borderColor = bmType === "erg" ? "cyan" : "orange";
					return bmList.length > 0 ? (
						<div
							className={`space-y-2 border border-dashed border-${borderColor}-400/30 rounded-sm p-3`}
						>
							<span
								className={`text-[10px] text-${borderColor}-400 uppercase tracking-wider font-semibold`}
							>
								{t("pbPaceReference")}
							</span>
							<div className="grid grid-cols-2 gap-2">
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("pbReferenceBenchmark")}
									</span>
									<select
										value={newExercise.pace_reference_benchmark_id || ""}
										onChange={(e) => {
											const bm = bmList.find(
												(b) => b.exercise_id === e.target.value,
											);
											setNewExercise({
												...newExercise,
												pace_reference_benchmark_id: e.target.value || null,
												pace_reference_benchmark_name: bm?.name || null,
											});
										}}
										className="w-full bg-secondary border border-border rounded-sm text-xs p-2"
									>
										<option value="">{t("noneOption")}</option>
										{bmList.map((bm) => (
											<option key={bm.exercise_id} value={bm.exercise_id}>
												{bm.name}
											</option>
										))}
									</select>
								</div>
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("pbPacePercent")}
									</span>
									<Input
										type="number"
										min={1}
										max={200}
										value={newExercise.pace_reference_percentage ?? ""}
										onChange={(e) =>
											setNewExercise({
												...newExercise,
												pace_reference_percentage: e.target.value
													? Number(e.target.value)
													: null,
											})
										}
										className="bg-secondary border-transparent rounded-sm text-xs"
										placeholder={t("pbEg85")}
									/>
								</div>
							</div>
							{newExercise.pace_reference_benchmark_id &&
								newExercise.pace_reference_percentage && (
									<p className={`text-[10px] text-${borderColor}-400/70`}>
										Al {newExercise.pace_reference_percentage}% del passo del{" "}
										{newExercise.pace_reference_benchmark_name || "benchmark"}
									</p>
								)}
						</div>
					) : null;
				})()}

			{/* Notes — for all bike types except Erg misto (ConditioningTemplateEditor already renders its own notes field) */}
			{newExercise.bike_type &&
				!(
					newExercise.exercise_type === "erg" &&
					isErgMixedType(newExercise.bike_type)
				) && (
					<div className="space-y-1">
						<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
							{t("notes")}
						</span>
						<Textarea
							value={newExercise.notes ?? ""}
							onChange={(e) =>
								setNewExercise({
									...newExercise,
									notes: e.target.value,
								})
							}
							className="bg-secondary border-transparent rounded-sm text-xs min-h-[48px] resize-none"
							placeholder={t("pbExtraNotesPlaceholder")}
						/>
					</div>
				)}
		</div>
	);
}
